import AssessmentIcon from "@mui/icons-material/Assessment";
import ShareIcon from "@mui/icons-material/Share";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { LiveView } from "../../components/live-view";
import { useWebSocketContext, WebSocketProvider } from "../../contexts/websocket-context";
import { useApiMutation, useTypedQueryParams } from "../../hooks";
import { useAutoScroll } from "../../hooks/use-auto-scroll";
import { useUrlModal } from "../../hooks/use-url-dialog";
import { BRAND_ACCENT } from "../../theme/theme";
import type { NewIssueData, NewIssueEvent } from "../../types";
import type { SuccessResponse } from "../../types/api";
import type {
  LandingInviteSchema,
  LandingSetupResponse,
  LandingSetupSchema,
} from "../../types/demo";
import type {
  TestAgentV2ConfigEvent,
  TestAgentV2Event,
  TestAgentV2IncomingEvent,
  TestAgentV2StatusEvent,
} from "../../types/test-agent-2";
import { clickableProps } from "../../utils/keyboard";
import { humanizeText } from "../../utils/text";
import { prettyPrintUrl } from "../../utils/url";

type LiveViewProps = {
  path: string;
  org_id: string;
};

const Loading = ({ text }: { text?: string }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="center"
    spacing={1}
    sx={{
      width: "100%",
      height: "100%",
      flex: 1,
      color: "common.white",
    }}
  >
    <CircularProgress size={16} sx={{ color: BRAND_ACCENT }} />
    <Typography
      variant="subtitle1"
      sx={{
        fontFamily:
          "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
        fontWeight: 500,
        fontSize: "20px",
        lineHeight: "1.2em",
        letterSpacing: "-0.04em",
        color: "common.white",
      }}
    >
      {text ?? "desplega.ai is loading..."}
    </Typography>
  </Stack>
);

export const DemoLiveViewInner = () => {
  const { messages } = useWebSocketContext<
    TestAgentV2IncomingEvent,
    TestAgentV2Event | NewIssueEvent
  >();
  const [selectedIssue, setSelectedIssue] = React.useState<NewIssueData | null>(null);

  // URL-based modal state (fixes back button behavior)
  const [reportDialogOpen, setReportDialogOpen] = useUrlModal("report");

  const configEvent = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].type === "config") {
        return messages[i].data as TestAgentV2ConfigEvent;
      }
    }
  }, [messages]);

  const lastStatus = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].type === "status") {
        return (messages[i].data as TestAgentV2StatusEvent).status;
      }
    }
    return "pending";
  }, [messages]);

  const issues = useMemo(() => {
    const rawIssues = messages
      .filter((msg) => msg.type === "new_issue")
      .map((msg) => msg as NewIssueEvent);

    // First, remove duplicate issues by ID
    const seenIds = new Set<string>();
    const uniqueIssues = rawIssues.filter(({ data: issue }) => {
      if (seenIds.has(issue.id)) {
        return false; // Skip this duplicate
      }
      seenIds.add(issue.id);
      return true; // Keep this unique issue
    });

    // Count issues with the same title
    const titleCounts = new Map<string, number>();
    uniqueIssues.forEach(({ data: issue }) => {
      const count = titleCounts.get(issue.title) || 0;
      titleCounts.set(issue.title, count + 1);
    });

    // Filter to only keep one entry per unique title and add count field
    const seenTitles = new Set<string>();
    return uniqueIssues
      .filter(({ data: issue }) => {
        if (seenTitles.has(issue.title)) {
          return false;
        }
        seenTitles.add(issue.title);
        return true;
      })
      .map(({ data: issue, ...rest }) => ({
        ...rest,
        data: {
          ...issue,
          count: titleCounts.get(issue.title) || 1,
        },
      }));
  }, [messages]);

  const totalInstances = useMemo(() => {
    return issues.reduce((sum, { data: issue }) => sum + (issue.count || 1), 0);
  }, [issues]);

  const truncateDescription = (description: string, maxLength: number = 200) => {
    // Filter out descriptions that are exactly "None"
    if (description === "None") {
      return "";
    }

    // Remove "None" from descriptions that contain it
    let cleanedDescription = description.replace(/\bNone\b/g, "").trim();

    // If the cleaned description is empty or just whitespace, return empty
    if (!cleanedDescription) {
      return "";
    }

    if (cleanedDescription.length <= maxLength) {
      return cleanedDescription;
    }

    // Middle ellipsis: take half from start and half from end
    cleanedDescription = `${cleanedDescription.substring(0, maxLength - 3)}...`;
    return cleanedDescription;
  };

  const handleIssueClick = (issue: NewIssueData) => {
    setSelectedIssue(issue);
    setReportDialogOpen(true);
  };

  const issuesRef = useRef<HTMLDivElement>(null);
  useAutoScroll(issuesRef.current, issues?.length);

  if (!configEvent) {
    return <Loading />;
  }

  return (
    <Stack direction="column" spacing={1} flex={1} alignItems="center">
      <Stack direction="column" spacing={2} alignItems="center" maxWidth="800px">
        <Typography
          variant="h5"
          sx={{
            fontFamily:
              "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
            fontWeight: 600,
            fontSize: "28px",
            lineHeight: "1.2em",
            letterSpacing: "-0.04em",
            color: BRAND_ACCENT,
            textAlign: "center",
          }}
        >
          Website Usability Analyzer
        </Typography>
        <Box sx={{ width: "100%", maxWidth: "800px", flex: 1 }}>
          <LiveView
            liveViewUrl={configEvent.liveview_url}
            allowInteractive={false}
            pageUrl={configEvent?.page_url || undefined}
            noLiveViewMessage="No live view available yet..."
            defaultHideControls
            height="360px"
          />
        </Box>
        <Stack
          direction="column"
          alignItems="stretch"
          width="100%"
          spacing={1}
          p={1}
          sx={{
            height: "300px",
            overflowY: "auto",
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(26px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "12px",
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              border: `1px solid ${BRAND_ACCENT}`,
            },
          }}
          ref={issuesRef}
        >
          <Typography
            variant="subtitle1"
            sx={{
              position: "sticky",
              top: 0,
              pt: 0,
              m: 0,
              background: "rgba(0, 0, 0, 0.5)",
              borderRadius: "8px",
              backdropFilter: "blur(5px)",
              zIndex: 1,
              p: 2,
              fontFamily:
                "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
              fontWeight: 500,
              fontSize: "20px",
              lineHeight: "1.2em",
              letterSpacing: "-0.04em",
              color: BRAND_ACCENT,
            }}
          >
            {issues.length} different issue{issues.length !== 1 ? "s" : ""} detected. (
            {totalInstances} instance
            {totalInstances !== 1 ? "s" : ""} found)
            {/* Status Chip */}
            <Chip
              label={humanizeText(lastStatus)}
              sx={{
                position: "absolute",
                bottom: 12,
                right: 12,
                fontFamily:
                  "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                fontWeight: 500,
                fontSize: "12px",
                lineHeight: "1.2em",
                letterSpacing: "-0.02em",
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                color: BRAND_ACCENT,
                border: "1px solid rgba(234, 255, 108, 0.3)",
                backdropFilter: "blur(8px)",
                "& .MuiChip-label": {
                  px: 1.5,
                  py: 0.5,
                },
              }}
            />
          </Typography>
          {issues.length > 0 &&
            issues.map(({ data: issue }, idx) => (
              <Stack
                key={idx}
                direction="row"
                spacing={2}
                p={2}
                sx={{
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "12px",
                  transition: "all 0.3s ease-in-out",
                  cursor: "pointer",
                  "&:hover": {
                    border: `1px solid ${BRAND_ACCENT}`,
                  },
                }}
                {...clickableProps(() => handleIssueClick(issue))}
                onClick={() => handleIssueClick(issue)}
              >
                {/* Left side: Title and Description */}
                <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontFamily:
                        "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                      fontWeight: 500,
                      fontSize: "15px",
                      lineHeight: "1.2em",
                      letterSpacing: "-0.04em",
                      color: "common.white",
                    }}
                  >
                    {issue.title}
                  </Typography>
                  {issue.description && truncateDescription(issue.description) && (
                    <Tooltip title={""}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily:
                            "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "1.3em",
                          letterSpacing: "-0.02em",
                          color: "rgba(255, 255, 255, 0.8)",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {truncateDescription(issue.description)}
                      </Typography>
                    </Tooltip>
                  )}
                </Stack>

                {/* Right side: URL, Count, and Tags */}
                <Stack
                  direction="column"
                  spacing={1}
                  sx={{ minWidth: "200px", alignItems: "flex-end" }}
                >
                  {issue.url && (
                    <Link
                      href={issue.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily:
                            "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                          fontWeight: 500,
                          fontSize: "14px",
                          lineHeight: "1.2em",
                          letterSpacing: "-0.04em",
                          color: BRAND_ACCENT,
                          "&:hover": {
                            color: BRAND_ACCENT,
                          },
                        }}
                      >
                        {prettyPrintUrl(issue.url)}
                      </Typography>
                    </Link>
                  )}

                  {issue.count && issue.count > 1 && (
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "8px",
                        backgroundColor: "rgba(234, 255, 108, 0.2)",
                        color: BRAND_ACCENT,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        fontFamily:
                          "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                        border: "1px solid rgba(234, 255, 108, 0.3)",
                      }}
                    >
                      {issue.count} instances
                    </Box>
                  )}

                  {issue.tags && issue.tags.length > 0 && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="flex-end"
                      flexWrap="wrap"
                    >
                      {issue.tags.map((tag, tagIdx) => (
                        <Box
                          key={tagIdx}
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: "6px",
                            backgroundColor: "rgba(234, 255, 108, 0.2)",
                            color: BRAND_ACCENT,
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            fontFamily:
                              "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                          }}
                        >
                          {tag}
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Stack>
            ))}
        </Stack>
      </Stack>

      {/* Report Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "grey.900",
            color: "common.white",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily:
              "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
            fontWeight: 600,
            fontSize: "24px",
            lineHeight: "1.2em",
            letterSpacing: "-0.04em",
            color: BRAND_ACCENT,
          }}
        >
          Issue Report
        </DialogTitle>
        <DialogContent>
          {selectedIssue && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily:
                      "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                    fontWeight: 600,
                    fontSize: "20px",
                    lineHeight: "1.2em",
                    letterSpacing: "-0.04em",
                    color: "common.white",
                  }}
                >
                  {selectedIssue.title}
                </Typography>
                {selectedIssue.count && selectedIssue.count > 1 && (
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: "8px",
                      backgroundColor: "rgba(234, 255, 108, 0.2)",
                      color: BRAND_ACCENT,
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      fontFamily:
                        "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                      border: "1px solid rgba(234, 255, 108, 0.3)",
                    }}
                  >
                    {selectedIssue.count} instances found
                  </Box>
                )}
              </Stack>

              {selectedIssue.url && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontFamily:
                        "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                      fontWeight: 600,
                      fontSize: "16px",
                      lineHeight: "1.2em",
                      letterSpacing: "-0.04em",
                      color: BRAND_ACCENT,
                      mb: 1,
                    }}
                  >
                    URL:
                  </Typography>
                  <Link
                    href={selectedIssue.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontFamily:
                        "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                      fontWeight: 500,
                      fontSize: "16px",
                      lineHeight: "1.2em",
                      letterSpacing: "-0.04em",
                      color: BRAND_ACCENT,
                      wordBreak: "break-all",
                      "&:hover": {
                        color: BRAND_ACCENT,
                      },
                    }}
                  >
                    {selectedIssue.url}
                  </Link>
                </Box>
              )}

              {selectedIssue.description && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontFamily:
                        "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                      fontWeight: 600,
                      fontSize: "16px",
                      lineHeight: "1.2em",
                      letterSpacing: "-0.04em",
                      color: BRAND_ACCENT,
                      mb: 1,
                    }}
                  >
                    Description:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily:
                        "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                      fontWeight: 400,
                      fontSize: "16px",
                      lineHeight: "1.5em",
                      letterSpacing: "-0.02em",
                      color: "rgba(255, 255, 255, 0.9)",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {selectedIssue.description}
                  </Typography>
                </Box>
              )}

              {selectedIssue.tags && selectedIssue.tags.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontFamily:
                        "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                      fontWeight: 600,
                      fontSize: "16px",
                      lineHeight: "1.2em",
                      letterSpacing: "-0.04em",
                      color: BRAND_ACCENT,
                      mb: 1,
                    }}
                  >
                    Tags:
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {selectedIssue.tags.map((tag: string, tagIdx: number) => (
                      <Box
                        key={tagIdx}
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "6px",
                          backgroundColor: "rgba(234, 255, 108, 0.2)",
                          color: BRAND_ACCENT,
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          fontFamily:
                            "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                        }}
                      >
                        {tag}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {selectedIssue.recommendations && selectedIssue.recommendations.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontFamily:
                        "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                      fontWeight: 600,
                      fontSize: "16px",
                      lineHeight: "1.2em",
                      letterSpacing: "-0.04em",
                      color: BRAND_ACCENT,
                      mb: 1,
                    }}
                  >
                    Recommendations:
                  </Typography>
                  <Stack spacing={1}>
                    {selectedIssue.recommendations.map((rec: string, recIdx: number) => (
                      <Typography
                        key={recIdx}
                        variant="body2"
                        sx={{
                          fontFamily:
                            "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                          fontWeight: 400,
                          fontSize: "15px",
                          lineHeight: "1.4em",
                          letterSpacing: "-0.02em",
                          color: "rgba(255, 255, 255, 0.8)",
                          pl: 2,
                          position: "relative",
                          "&::before": {
                            content: '"•"',
                            position: "absolute",
                            left: 0,
                            color: BRAND_ACCENT,
                          },
                        }}
                      >
                        {rec}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setReportDialogOpen(false)}
            sx={{
              fontFamily:
                "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
              fontWeight: 600,
              fontSize: "16px",
              lineHeight: "1.2em",
              letterSpacing: "-0.04em",
              background: BRAND_ACCENT,
              color: "common.black",
              borderRadius: "8px",
              px: 3,
              "&:hover": {
                background: BRAND_ACCENT,
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

const DemoLiveView = ({ path, org_id }: LiveViewProps) => {
  console.log({
    path,
    org_id,
  });

  return (
    <Box data-testid="demo-live-view">
      <WebSocketProvider<TestAgentV2IncomingEvent, TestAgentV2Event | NewIssueEvent>
        key={org_id}
        path={path}
        useAuth={false}
      >
        <DemoLiveViewInner />
      </WebSocketProvider>
    </Box>
  );
};

const DemoLanding1Inner = () => {
  const [manualUrl, setManualUrl] = React.useState<string | null>(null);
  const [url, setUrl] = React.useState<string | null>(null);

  const { params, setParam } = useTypedQueryParams<{ url?: string }>();

  useEffect(() => {
    if (!params?.url) return;

    // Normalize URL: remove everything before // and prepend https://
    const trimmedUrl = params.url.trim();
    const urlWithoutProtocol = trimmedUrl.includes("//")
      ? trimmedUrl.substring(trimmedUrl.indexOf("//") + 2)
      : trimmedUrl;

    setUrl(`https://${urlWithoutProtocol}`);
  }, [params?.url]);

  const [email, setEmail] = React.useState("");

  // URL-based modal state (fixes back button behavior)
  const [dialogOpen, setDialogOpen] = useUrlModal("invite");

  const [actionType, setActionType] = React.useState<"share" | "report" | null>(null);
  const [issuesUrl, setIssuesUrl] = React.useState<string | null>(null);

  const {
    mutate: setupLanding,
    data: landingData,
    isPending: landingPending,
  } = useApiMutation<LandingSetupSchema, LandingSetupResponse>("POST", "/demo/landing-setup");

  const { mutate: inviteUser, isPending: invitePending } = useApiMutation<
    LandingInviteSchema,
    SuccessResponse
  >("POST", "/demo/landing-invite");

  const isValidUrl = useCallback((_url: string) => {
    try {
      new URL(_url);
      return true;
    } catch {
      return false;
    }
  }, []);

  const handleButtonClick = (type: "share" | "report") => {
    setActionType(type);
    setDialogOpen(true);
  };

  const handleInviteSubmit = () => {
    if (!landingData || !email) return;

    inviteUser({
      org_id: landingData.org_id,
      email,
      seed_id: landingData.seed_id,
    });

    setIssuesUrl(landingData.issues_url);

    // Copy to clipboard
    navigator.clipboard.writeText(landingData.issues_url);

    // Don't close the dialog automatically - let user close it
  };

  // Auto-trigger setup if URL is provided and valid
  React.useEffect(() => {
    if (url && isValidUrl(url) && !landingData && !landingPending) {
      setupLanding({ url });
    }
  }, [url, isValidUrl, landingData, landingPending, setupLanding]);

  if (landingPending) {
    return <Loading text="Setting up your demo..." />;
  }

  if (!url) {
    return (
      <Box sx={{ width: "100%", height: "100%" }}>
        <TextField
          variant="outlined"
          sx={{
            "& .MuiInputLabel-root": {
              color: "common.white",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: BRAND_ACCENT,
            },
            "& .MuiOutlinedInput-root": {
              borderColor: "rgba(255, 255, 255, 0.1)",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              borderRadius: 2,
              color: "common.white",
            },
            "& .MuiOutlinedInput-root:hover": {
              "& > fieldset": {
                borderColor: "common.white",
              },
            },
            "& .MuiOutlinedInput-root.Mui-focused": {
              "& > fieldset": {
                borderColor: BRAND_ACCENT,
                borderWidth: 1,
              },
            },
          }}
          value={manualUrl || ""}
          onChange={(e) => setManualUrl(e.target.value)}
          placeholder="Enter a URL to test (e.g., https://example.com)"
          fullWidth
          label="Please provide a URL to test"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && manualUrl && isValidUrl(manualUrl)) {
              setParam("url", manualUrl);
            }
          }}
          InputLabelProps={{
            shrink: true,
          }}
          slotProps={{
            input: {
              endAdornment: manualUrl && isValidUrl(manualUrl) && (
                <Typography
                  variant="body2"
                  sx={{
                    minWidth: "fit-content",
                    cursor: "pointer",
                    userSelect: "none",
                    color: BRAND_ACCENT,
                    fontFamily:
                      "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                  }}
                >
                  Press ↵ to test
                </Typography>
              ),
            },
          }}
        />
      </Box>
    );
  }

  if (url && !isValidUrl) {
    return (
      <Typography
        variant="h6"
        align="center"
        sx={{
          fontFamily:
            "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
          fontWeight: 500,
          fontSize: "20px",
          lineHeight: "1.2em",
          letterSpacing: "-0.04em",
          color: "error.main",
        }}
      >
        Invalid URL provided: {url}
      </Typography>
    );
  }

  if (landingData) {
    return (
      <>
        <Stack direction="column" spacing={2} zIndex={1000}>
          <DemoLiveView path={landingData.live_demo_url} org_id={landingData.org_id} />
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              startIcon={<AssessmentIcon />}
              onClick={() => handleButtonClick("report")}
              sx={{
                borderRadius: "36px",
                minWidth: 160,
                fontFamily:
                  "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: "1.2em",
                letterSpacing: "-0.04em",
                background: "transparent",
                border: `1px solid ${BRAND_ACCENT}`,
                color: BRAND_ACCENT,
                "&:hover": {
                  background: BRAND_ACCENT,
                  border: `1px solid ${BRAND_ACCENT}`,
                  color: "common.black",
                },
              }}
            >
              full report
            </Button>
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={() => handleButtonClick("share")}
              sx={{
                borderRadius: "36px",
                minWidth: 120,
                fontFamily:
                  "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: "1.2em",
                letterSpacing: "-0.04em",
                background: "transparent",
                border: `1px solid ${BRAND_ACCENT}`,
                color: BRAND_ACCENT,
                "&:hover": {
                  background: BRAND_ACCENT,
                  border: `1px solid ${BRAND_ACCENT}`,
                  color: "common.black",
                },
              }}
            >
              Share
            </Button>
          </Stack>
        </Stack>

        {/* Email Input Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: "grey.900",
              color: "common.white",
              borderRadius: "12px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <DialogTitle
            sx={{
              fontFamily:
                "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
              fontWeight: 600,
              fontSize: "24px",
              lineHeight: "1.2em",
              letterSpacing: "-0.04em",
              color: BRAND_ACCENT,
            }}
          >
            {actionType === "report" ? "Full Report access" : "Share your results"}
          </DialogTitle>
          <DialogContent>
            {!issuesUrl ? (
              <TextField
                autoFocus
                margin="dense"
                label="Email address"
                placeholder="Enter your email to access the full report"
                type="email"
                fullWidth
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={invitePending}
                sx={{
                  mt: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    "&:hover": {
                      border: `.5px solid ${BRAND_ACCENT}`,
                    },
                    "&.Mui-focused": {
                      border: "0",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: BRAND_ACCENT,
                        borderWidth: "1",
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.1)",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily:
                      "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "1.2em",
                    letterSpacing: "-0.04em",
                    color: BRAND_ACCENT,
                    "&.Mui-focused": {
                      color: BRAND_ACCENT,
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontFamily:
                      "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "1.2em",
                    letterSpacing: "-0.04em",
                    color: "common.white",
                  },
                }}
              />
            ) : (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily:
                      "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "1.2em",
                    letterSpacing: "-0.04em",
                    color: "rgba(255, 255, 255, 0.8)",
                    mb: 2,
                  }}
                >
                  The link has been copied to your clipboard and is ready to use:
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <Link
                    href={issuesUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontFamily:
                        "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                      fontWeight: 500,
                      fontSize: "16px",
                      lineHeight: "1.2em",
                      letterSpacing: "-0.04em",
                      color: BRAND_ACCENT,
                      wordBreak: "break-all",
                      "&:hover": {
                        color: BRAND_ACCENT,
                      },
                    }}
                  >
                    {issuesUrl}
                  </Link>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            {!issuesUrl ? (
              <>
                <Button
                  onClick={() => setDialogOpen(false)}
                  sx={{
                    fontFamily:
                      "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "1.2em",
                    letterSpacing: "-0.04em",
                    color: "rgba(255, 255, 255, 0.7)",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleInviteSubmit}
                  disabled={!email || invitePending}
                  sx={{
                    fontFamily:
                      "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                    fontWeight: 600,
                    fontSize: "16px",
                    lineHeight: "1.2em",
                    letterSpacing: "-0.04em",
                    background: BRAND_ACCENT,
                    color: "common.black",
                    borderRadius: "8px",
                    px: 3,
                    "&:hover": {
                      background: BRAND_ACCENT,
                    },
                    "&:disabled": {
                      background: "rgba(255, 255, 255, 0.3)",
                      color: "rgba(255, 255, 255, 0.5)",
                    },
                  }}
                >
                  {invitePending ? "Sending..." : actionType === "report" ? "See Report" : "Share"}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => setDialogOpen(false)}
                sx={{
                  fontFamily:
                    "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                  fontWeight: 600,
                  fontSize: "16px",
                  lineHeight: "1.2em",
                  letterSpacing: "-0.04em",
                  background: BRAND_ACCENT,
                  color: "common.black",
                  borderRadius: "8px",
                  px: 3,
                  "&:hover": {
                    background: BRAND_ACCENT,
                  },
                }}
              >
                Close
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      {!url && (
        <Typography
          variant="h6"
          align="center"
          sx={{
            fontFamily:
              "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
            fontWeight: 500,
            fontSize: "20px",
            lineHeight: "1.2em",
            letterSpacing: "-0.04em",
            color: "common.white",
          }}
        >
          Please provide a URL parameter to test a website
        </Typography>
      )}
      {url && !isValidUrl && (
        <Typography
          variant="h6"
          align="center"
          sx={{
            fontFamily:
              "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
            fontWeight: 500,
            fontSize: "20px",
            lineHeight: "1.2em",
            letterSpacing: "-0.04em",
            color: "error.main",
          }}
        >
          Invalid URL provided: {url}
        </Typography>
      )}
    </>
  );
};

export const DemoLanding1Page = () => {
  return (
    <Box
      data-testid="demo-landing-1-page"
      sx={{
        position: "relative",
        overflow: "hidden",
        mx: "auto",
        p: 4,
        minHeight: "100vh",
        bgcolor: "common.black",
        color: "common.white",
        fontFamily:
          "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      }}
    >
      {/* Background Elements */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        {/* Blue circle */}
        <Box
          sx={{
            position: "absolute",
            borderRadius: "50%",
            bgcolor: "primary.main", // desplega-primary equivalent
            left: "-15%",
            top: "20%",
            width: "40%",
            height: "40%",
            filter: "blur(120px)",
          }}
        />
        {/* Yellow circle */}
        <Box
          sx={{
            position: "absolute",
            borderRadius: "50%",
            background: BRAND_ACCENT, // desplega-secondary equivalent
            right: "-10%",
            top: "10%",
            width: "30%",
            height: "30%",
            filter: "blur(100px)",
          }}
        />
      </Box>

      <DemoLanding1Inner />
    </Box>
  );
};
